type AriaListboxConfig = {
  handleFocusChange?: (isFocused: boolean, element: HTMLElement) => void;
};

export class AriaListbox {
  listboxNode: HTMLElement;
  activeDescendant: string | null;
  multiselectable: boolean;
  moveUpDownEnabled: boolean;
  siblingList: HTMLElement[] | null;
  startRangeIndex: number | null;
  keysSoFar: string;
  handleFocusChange: Function;
  handleItemChange: Function;

  constructor(listboxNode: HTMLElement, { handleFocusChange }: AriaListboxConfig = {}) {
    this.listboxNode = listboxNode;
    this.activeDescendant = this.listboxNode.getAttribute('aria-activedescendant');
    this.multiselectable = this.listboxNode.hasAttribute('aria-multiselectable');
    this.moveUpDownEnabled = false;
    this.siblingList = null;
    this.startRangeIndex = 0;
    this.keysSoFar = '';
    this.handleFocusChange = handleFocusChange ?? function () {};
    this.handleItemChange = function () {};
    this.registerEvents();

    if (document.activeElement === this.listboxNode) this.setupFocus();
  }

  registerEvents() {
    this.listboxNode.addEventListener('focus', this.setupFocus.bind(this));
    this.listboxNode.addEventListener('focusout', this.onFocusOut.bind(this));
    this.listboxNode.addEventListener('keydown', this.checkKeyPress.bind(this));
    this.listboxNode.addEventListener('click', this.checkClickItem.bind(this));

    if (this.multiselectable) {
      this.listboxNode.addEventListener('mousedown', this.checkMouseDown.bind(this));
    }
  }

  unregisterEvents() {
    this.listboxNode.removeEventListener('focus', this.setupFocus.bind(this));
    this.listboxNode.removeEventListener('focusout', this.onFocusOut.bind(this));
    this.listboxNode.removeEventListener('keydown', this.checkKeyPress.bind(this));
    this.listboxNode.removeEventListener('click', this.checkClickItem.bind(this));
    this.listboxNode.removeEventListener('mousedown', this.checkMouseDown.bind(this));
  }

  setupFocus() {
    if (this.activeDescendant) {
      const listitem = document.getElementById(this.activeDescendant);
      setTimeout(() => listitem?.scrollIntoView({ block: 'nearest', inline: 'nearest' }), 200);
      if (listitem) this.focusItem(listitem);
    } else {
      const firstItem = this.focusFirstItem();
      setTimeout(() => firstItem?.scrollIntoView({ block: 'nearest', inline: 'nearest' }), 200);
    }
  }

  onFocusOut(e: FocusEvent) {
    if (!this.activeDescendant || !(e.relatedTarget instanceof HTMLElement)) return;

    const listitem = document.getElementById(this.activeDescendant);

    if (this.listboxNode.contains(e.relatedTarget)) return;

    this.defocusItem(listitem);
    this.activeDescendant = null;
  }

  focusFirstItem() {
    const firstItem = this.listboxNode.querySelector<HTMLElement>('[role="option"]');

    if (firstItem) {
      this.focusItem(firstItem);
    }

    return firstItem;
  }

  focusLastItem() {
    const itemList = this.listboxNode.querySelectorAll<HTMLElement>('[role="option"]');

    if (itemList.length) {
      this.focusItem(itemList[itemList.length - 1]);
    }
  }

  checkKeyPress(evt: KeyboardEvent) {
    const lastActiveId = this.activeDescendant;
    const allOptions = this.listboxNode.querySelectorAll<HTMLElement>('[role="option"]');
    const currentItem = this.activeDescendant
      ? document.getElementById(this.activeDescendant) || allOptions[0]
      : allOptions[0];
    let nextItem = currentItem;

    if (!currentItem || !nextItem || document.activeElement !== this.listboxNode) return;

    switch (evt.key) {
      case 'ArrowUp':
      case 'ArrowDown':
        evt.preventDefault();
        if (!this.activeDescendant) {
          // focus first option if no option was previously focused, and perform no other actions
          this.focusItem(currentItem);
          break;
        }

        if (evt.key === 'ArrowUp') {
          nextItem = this.findPreviousOption(currentItem);
        } else {
          nextItem = this.findNextOption(currentItem);
        }

        if (nextItem) {
          this.focusItem(nextItem);
        }

        break;

      case 'Home':
        evt.preventDefault();
        this.focusFirstItem();
        break;

      case 'End':
        evt.preventDefault();
        this.focusLastItem();
        break;

      case 'Shift':
        this.startRangeIndex = this.getElementIndex(currentItem, allOptions);
        break;

      case ' ':
      case 'Enter':
        evt.preventDefault();
        nextItem.click();
        this.toggleSelectItem(nextItem);
        break;
      // fall through
      default:
        if (evt.key.length === 1) {
          const itemToFocus = this.findItemToFocus(evt.key.toLowerCase());
          if (itemToFocus) {
            this.focusItem(itemToFocus);
          }
        }
        break;
    }

    if (this.activeDescendant !== lastActiveId) {
      this.updateScroll();
    }
  }

  findItemToFocus(character: string) {
    const itemList = this.listboxNode.querySelectorAll<HTMLElement>('[role="option"]');
    let searchIndex = 0;

    if (!this.keysSoFar) {
      for (let i = 0; i < itemList.length; i++) {
        if (itemList[i].getAttribute('id') === this.activeDescendant) {
          searchIndex = i;
        }
      }
    }

    this.keysSoFar += character;

    let nextMatch = this.findMatchInRange(itemList, searchIndex + 1, itemList.length);

    if (!nextMatch) {
      nextMatch = this.findMatchInRange(itemList, 0, searchIndex);
    }
    return nextMatch;
  }

  /* Return the index of the passed element within the passed array, or null if not found */
  getElementIndex(option: HTMLElement, options: NodeListOf<HTMLElement>) {
    const allOptions = Array.prototype.slice.call(options); // convert to array
    const optionIndex = allOptions.indexOf(option);

    return typeof optionIndex === 'number' ? optionIndex : null;
  }

  /* Return the next listbox option, if it exists; otherwise, returns null */
  findNextOption(currentOption: HTMLElement) {
    const allOptions = Array.prototype.slice.call(this.listboxNode.querySelectorAll('[role="option"]')); // get options array
    const currentOptionIndex = allOptions.indexOf(currentOption);
    let nextOption = null;

    if (currentOptionIndex > -1 && currentOptionIndex < allOptions.length - 1) {
      nextOption = allOptions[currentOptionIndex + 1];
    }

    return nextOption;
  }

  /* Return the previous listbox option, if it exists; otherwise, returns null */
  findPreviousOption(currentOption: HTMLElement) {
    const allOptions = Array.prototype.slice.call(this.listboxNode.querySelectorAll('[role="option"]')); // get options array
    const currentOptionIndex = allOptions.indexOf(currentOption);
    let previousOption = null;

    if (currentOptionIndex > -1 && currentOptionIndex > 0) {
      previousOption = allOptions[currentOptionIndex - 1];
    }

    return previousOption;
  }

  findMatchInRange(list: NodeListOf<HTMLElement>, startIndex: number, endIndex: number) {
    // Find the first item starting with the keysSoFar substring, searching in
    // the specified range of items
    for (let n = startIndex; n < endIndex; n++) {
      const label = list[n].innerText;
      if (label && label.toLowerCase().indexOf(this.keysSoFar) === 0) {
        return list[n];
      }
    }
    return null;
  }

  checkClickItem(evt: MouseEvent) {
    if (!(evt.target instanceof HTMLElement)) return;

    if (evt.target?.getAttribute('role') !== 'option') {
      return;
    }

    this.focusItem(evt.target);
    this.toggleSelectItem(evt.target);
  }

  /**
   * Prevent text selection on shift + click for multi-select listboxes
   *
   * @param evt
   */
  checkMouseDown(evt: MouseEvent) {
    if (this.multiselectable && evt.shiftKey && (evt.target as HTMLElement).getAttribute('role') === 'option') {
      evt.preventDefault();
    }
  }

  /**
   * @description
   *  Toggle the aria-selected value
   * @param element
   *  The element to select
   */
  toggleSelectItem(element: HTMLElement) {
    if (this.multiselectable) {
      element.setAttribute('aria-selected', element.getAttribute('aria-selected') === 'true' ? 'false' : 'true');
    }
  }

  /**
   * @description
   *  Defocus the specified item
   * @param element
   *  The element to defocus
   */
  defocusItem(element: HTMLElement | null) {
    if (!element) return;

    if (!this.multiselectable) {
      element.removeAttribute('aria-selected');
    }
    element.classList.remove('focused');

    this.handleFocusChange(false, element);
  }

  /**
   * @description
   *  Focus on the specified item
   * @param element
   *  The element to focus
   */
  focusItem(element: HTMLElement) {
    if (this.activeDescendant && this.activeDescendant !== element.id)
      this.defocusItem(document.getElementById(this.activeDescendant));

    if (!this.multiselectable) {
      element.setAttribute('aria-selected', 'true');
    }

    if (this.listboxNode.matches(':focus-visible')) element.classList.add('focused');

    this.listboxNode.setAttribute('aria-activedescendant', element.id);
    this.activeDescendant = element.id;

    this.handleFocusChange(true, element);
  }

  /**
   * Helper function to check if a number is within a range; no side effects.
   */
  checkInRange(index: number, start: number, end: number) {
    const rangeStart = start < end ? start : end;
    const rangeEnd = start < end ? end : start;

    return index >= rangeStart && index <= rangeEnd;
  }

  /**
   * Check if the selected option is in view, and scroll if not
   */
  updateScroll() {
    const selectedOption = this.activeDescendant ? document.getElementById(this.activeDescendant) : null;

    if (selectedOption) {
      const scrollBottom = this.listboxNode.clientHeight + this.listboxNode.scrollTop;
      const elementBottom = selectedOption.offsetTop + selectedOption.offsetHeight;
      if (elementBottom > scrollBottom) {
        this.listboxNode.scrollTop = elementBottom - this.listboxNode.clientHeight;
      } else if (selectedOption.offsetTop < this.listboxNode.scrollTop) {
        this.listboxNode.scrollTop = selectedOption.offsetTop;
      }
      selectedOption.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  setActiveDescendant(elementId: string) {
    this.activeDescendant = elementId;
    this.listboxNode.setAttribute('aria-activedescendant', elementId);
  }
}
